/**
 * Unified Checkout adapter (H2).
 *
 * The only place in `apps/web` that touches the CyberSource client library.
 * Everything above it — the checkout state machine, the challenge component,
 * the tests — depends on {@link UnifiedCheckoutAdapter}, never on the global
 * `window.Accept`, so the whole flow can be exercised with a fake and no test
 * touches the network.
 *
 * ## The API shape, which is not the obvious one
 *
 * Unified Checkout v1 is a three-step async chain, and getting any step wrong
 * produces a **blank panel with no error**:
 *
 * ```js
 * const accept = await window.Accept(captureContext);  // 1. the context
 * const up     = await accept.unifiedPayments(false);  // 2. the module
 * const token  = await up.show({ containers: {...} });  // 3. the form + token
 * ```
 *
 * `window.Challenge.instance.accept()` is the **Microform** API and is a
 * different product. It was what this file originally spoke, which is why a
 * deployed donor site showed no payment form at all: nothing was ever asked to
 * render one, and nothing threw, so there was nothing to see. The reference
 * implementation in `reference/old-cybersource/components/CheckoutForm.ts`
 * is what this now matches.
 *
 * ## Why the containers must exist first
 *
 * `show()` does not create a frame. It populates the two elements named in
 * `containers`, and it does so as soon as it is called. An empty selector
 * resolves to nothing and the form is silently rendered nowhere. The page
 * therefore renders `#uc-payment-selection` and `#uc-payment-screen`
 * unconditionally, before any script runs.
 *
 * ## Why `crossOrigin` is set
 *
 * A script with an `integrity` attribute is fetched in CORS mode, and the
 * browser refuses to evaluate it unless the response carries
 * `Access-Control-Allow-Origin`. CyberSource's `SecureAcceptance.js` is served
 * from `https://testup.cybersource.com`, a different origin from the donor
 * site, so without `crossOrigin="anonymous"` the integrity check cannot be
 * performed and the browser blocks the script:
 *
 * > Subresource Integrity: The resource '…/SecureAcceptance.js' has an
 * > integrity attribute, but the resource requires the request to be CORS
 * > enabled to check the integrity, and it is not.
 *
 * Setting the attribute is what makes the load CORS-enabled. It is not a
 * weakening of anything: the integrity hash still decides whether the bytes that
 * arrived are the bytes the signed capture context named.
 */

import { debugFrame, debugTimer } from './debugLog';

/** The three values the capture-context endpoint returns. */
export interface CaptureContextResponse {
  /** The signed context, handed straight to `window.Accept`. */
  captureContext: string;
  /** SRI hash the library script must satisfy. */
  clientLibraryIntegrity: string;
  /** URL the library is loaded from. */
  clientLibrary: string;
  /**
   * The Decision Manager device-fingerprint script URL, when the server can
   * build one.
   *
   * Optional on purpose: without an org id in the environment the server has
   * nowhere to file a fingerprint, and the honest answer is "no script" rather
   * than a script that 404s. The checkout proceeds without it either way.
   */
  fingerprintScriptUrl?: string;
}

/** `unifiedPayments(false)` — the module that renders the payment form. */
export interface UnifiedPaymentsModule {
  /** Renders the payment form and resolves with the transient token. */
  show(options: { containers: UCContainers }): Promise<string>;
  dispose?: () => void;
}

/** The two elements the library populates. Both must already be in the DOM. */
export interface UCContainers {
  paymentSelection: string;
  paymentScreen: string;
}

/** What `window.Accept(captureContext)` resolves with. */
export type AcceptModule = {
  unifiedPayments(autoSubmit: boolean): Promise<UnifiedPaymentsModule>;
};

/**
 * `window.Accept` itself — the function the library installs, not its result.
 *
 * Named `factory` because passing the *module* where the *function* belongs is
 * the sort of confusion that produces a blank payment form.
 */
export type AcceptFactory = (captureContext: string) => Promise<AcceptModule>;

/** The global the client library installs on `window`. */
export interface ChallengeGlobal {
  Accept?: AcceptFactory;
}

/** The selectors the page must render before `show()` is called. */
export const UC_CONTAINERS: UCContainers = {
  paymentSelection: '#uc-payment-selection',
  paymentScreen: '#uc-payment-screen',
};

/**
 * The CSS the two containers need, so they exist as real styles rather than
 * classes a build step might drop.
 *
 * A Unified Checkout frame renders nothing visible into a container with zero
 * height. The symptom is `show()` resolving and the donor seeing an empty panel,
 * which is indistinguishable from a broken gateway and costs the same afternoon
 * to diagnose. The owner's live run confirmed it: the frame was refused because
 * the container it was to populate had no box to populate.
 *
 * Written here as a literal rather than as utility classes so it cannot be
 * tree-shaken away by a purger that has never seen these ids.
 */
export const UC_CONTAINER_CSS = `
.uc-mount { width: 100%; }
#uc-payment-selection { width: 100%; }
#uc-payment-screen {
  width: 100%;
  /* The card iframe is roughly this tall; without a floor the container
     collapses to nothing and the form has nowhere to draw. */
  min-height: 400px;
}
`;

export type UnifiedCheckoutFailure =
  /** The `<script>` did not load, or the CSP blocked it. */
  | 'script_blocked'
  /** The library loaded but never defined `window.Accept`. */
  | 'accept_missing'
  /** `Accept()` or `unifiedPayments()` refused. */
  | 'accept_failed'
  /** `show()` refused — usually a CSP `frame-src` refusal. */
  | 'show_failed'
  /**
   * The card frame never produced a token in time.
   *
   * Its own code, and not a flavour of `show_failed`, because the two need
   * different words and different buttons. A CSP refusal is broken for
   * everybody and a reload is the honest answer; a frame that simply did not
   * answer in time is one bad moment, the donor's card was never charged, and
   * the right thing is a **retry** that reuses the donation they already
   * started. Telling a donor to reload after a timeout throws away the
   * donation and asks them to type their card again.
   */
  | 'show_timeout'
  /** `show()` resolved with no transient token. */
  | 'no_transient_token'
  /** The capture context response was incomplete. */
  | 'context_incomplete';

export class UnifiedCheckoutError extends Error {
  override name = 'UnifiedCheckoutError';
  /**
   * A closed set, so the page can render a *specific* message.
   *
   * "The payment form did not appear" is the single most useful thing to tell a
   * donor when something is wrong, and a generic failure message is the least
   * useful. Each code maps to a distinct sentence, and the mapping lives with
   * the strings rather than in a component.
   */
  readonly code: UnifiedCheckoutFailure;
  /** Safe to show a donor: what actually happened, in plain words. */
  readonly detail: string;

  constructor(code: UnifiedCheckoutFailure, detail: string) {
    super(`${code}: ${detail}`);
    this.code = code;
    this.detail = detail;
  }
}

export interface UnifiedCheckoutAdapter {
  /**
   * Load the library, render the payment form into its containers, and return
   * the transient token the capture and Payer Auth calls need.
   */
  accept(captureContextResponse: CaptureContextResponse): Promise<string>;
}

/** Injectable seams, so a test can drive the real loader with a fake transport. */
export interface AdapterOptions {
  inject: (url: string, integrity: string) => Promise<void>;
  /**
   * Starts the Decision Manager fingerprint script. Injectable so a test can
   * assert *that it started and with which session id* without a network request,
   * and without asserting on DOM side effects of a script that is designed not
   * to be awaited.
   */
  injectFingerprint?: (url: string) => Promise<void>;
  /** Resolves once `window.Accept` exists, rejects after `acceptTimeoutMs`. */
  waitForAccept: (acceptTimeoutMs: number) => Promise<AcceptFactory>;
  /** The global the library installs into; injectable so a test need not touch `window`. */
  scope?: ChallengeGlobal;
  /** How long to wait for `window.Accept` before calling it blocked. */
  acceptTimeoutMs?: number;
  /** How long to wait for `show()` to produce a token. */
  showTimeoutMs?: number;
}

/** How long `window.Accept` has to appear before we call it blocked. */
const DEFAULT_ACCEPT_TIMEOUT_MS = 15_000;

/**
 * How long `show()` has to resolve.
 *
 * Generous on purpose: `show()` renders a hosted iframe, and on a slow
 * connection or a cold Cardinal connection that is not instant. Timing out too
 * eagerly would tell a donor the payment failed while it is still loading.
 *
 * Configurable, because the right value depends on the box and the day: a
 * slow VPS wants longer than a laptop on fibre, and the owner should not have
 * to rebuild the donor site to change it. `VITE_UC_SHOW_TIMEOUT_MS` sets it in
 * milliseconds; anything unparseable falls back to this default rather than to
 * zero, because a zero timeout would fail every donor instantly.
 */
export const DEFAULT_SHOW_TIMEOUT_MS = 60_000;

/**
 * Read the card-frame timeout out of an environment value.
 *
 * A pure function so the rule can be tested without a build: the value is a
 * number of milliseconds, it must be positive, and anything else - an empty
 * string, a word, a negative number, a fraction - is a configuration mistake
 * that must not become a donor-facing failure.
 */
export function showTimeoutFromEnv(raw: string | undefined): number {
  if (typeof raw !== 'string') return DEFAULT_SHOW_TIMEOUT_MS;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return DEFAULT_SHOW_TIMEOUT_MS;
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_SHOW_TIMEOUT_MS;
  return parsed;
}

/** Poll interval while waiting for the global. */
const ACCEPT_POLL_MS = 50;

/**
 * Append a `<script>` for the library and resolve on load.
 *
 * This is the REAL loader — the one that runs in a browser. It is exported so a
 * test can exercise it directly rather than asserting against a mock: the
 * integrity/CORS bug this file exists to fix lived exactly here, and a test that
 * mocked the loader could not have caught it.
 *
 * `crossOrigin = 'anonymous'` is the fix. Without it the integrity check
 * requires a CORS-enabled request that never happens and the browser blocks the
 * script with no error the page can see.
 */
export function defaultInjectScript(url: string, integrity: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    // Set through the content attribute rather than the IDL property.
    //
    // In a browser the two are equivalent for SRI, but jsdom does not implement
    // the `integrity` property at all: assigning it leaves a stray JavaScript
    // property and creates no attribute, so a test reading
    // `getAttribute('integrity')` would see `null` and the attribute the browser
    // acts on would be untested. `setAttribute` puts the value where the
    // browser actually reads it, in both environments.
    script.setAttribute('integrity', integrity);
    // Without this the integrity check needs a CORS-enabled request that a
    // cross-origin <script> does not make, and the browser blocks the file with
    // no error the page can see.
    script.setAttribute('crossorigin', 'anonymous');
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () =>
      reject(
        new UnifiedCheckoutError(
          'script_blocked',
          'The secure payment library could not be loaded. This is usually a Content Security Policy blocking https://testup.cybersource.com.',
        ),
      ),
    );
    document.head.append(script);
  });
}

/**
 * Load the Decision Manager device-fingerprint script.
 *
 * ## Why this is separate from the library loader
 *
 * The two scripts have different rules, and getting them confused is a silent
 * failure:
 *
 *  - **No SRI.** CyberSource serves `fp/tags.js` with a rotating `org_id` query,
 *    and the file is not published with a stable hash. Inventing a hash here
 *    would make the browser refuse a legitimate script, which presents as "no
 *    fingerprint" on every order — the exact state the owner found in Business
 *    Center.
 *  - **No wait for it.** The library's load must be awaited, because the pay
 *    button cannot work without it. The fingerprint's collection runs in the
 *    background for 3–5 seconds after the page renders; the checkout is not
 *    supposed to wait for it and must never be blocked by it.
 *  - **Failure is not an error.** A blocked fingerprint script costs a risk
 *    signal. A blocked payment library costs the donation. Only the second is
 *    worth interrupting a donor over, so this resolves either way.
 *
 * ## Where it is loaded
 *
 * CyberSource's Decision Manager integration guide is explicit that the script
 * goes in *"when the checkout page renders, not in the Pay button handler"*. A
 * donor who sits on the amount screen for a minute is giving Decision Manager
 * something to profile; a donor who goes straight from render to submit is not.
 * That is why the caller runs this at page level.
 */
export function injectFingerprintScript(url: string): Promise<void> {
  return new Promise<void>((resolve) => {
    // A page can render more than once (React strict mode, a remount, a route
    // change back to the checkout). Loading the same session twice collects
    // twice and muddies the profile, so the second load is skipped.
    if (document.querySelector(`script[src="${CSS.escape(url)}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    // Absent, deliberately: see above. `crossorigin` is set for the same reason
    // as the library loader — a cross-origin script without it is fetched in a
    // mode the profiling endpoint does not answer.
    script.setAttribute('crossorigin', 'anonymous');
    script.addEventListener('load', () => resolve());
    // Resolving on error is the whole point. This script is a signal, not a
    // dependency.
    script.addEventListener('error', () => resolve());
    document.head.append(script);
  });
}

/**
 * Resolve once `window.Accept` exists.
 *
 * Polls rather than trusting the `load` event alone, because the library sets
 * its global during evaluation and the two are not ordered the same way for a
 * cached asset and a first load. The deadline turns "never" into a specific
 * `accept_missing` failure rather than a page that spins forever.
 */
export function defaultWaitForAccept(
  acceptTimeoutMs: number = DEFAULT_ACCEPT_TIMEOUT_MS,
  scope: ChallengeGlobal = window as unknown as ChallengeGlobal,
): Promise<AcceptFactory> {
  return new Promise<AcceptFactory>((resolve, reject) => {
    const deadline = Date.now() + acceptTimeoutMs;
    const poll = (): void => {
      const accept = scope.Accept;
      if (typeof accept === 'function') {
        resolve(accept);
        return;
      }
      if (Date.now() >= deadline) {
        reject(
          new UnifiedCheckoutError(
            'accept_missing',
            'The secure payment library loaded but never started. Check that scripts from ' +
              'https://testup.cybersource.com are allowed by the Content Security Policy.',
          ),
        );
        return;
      }
      setTimeout(poll, ACCEPT_POLL_MS);
    };
    poll();
  });
}

/** Reject with a specific code if `work` has not settled in `ms`. */
function withTimeout<T>(work: Promise<T>, ms: number, onTimeout: () => Error): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(onTimeout());
    }, ms);
    work.then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Turn anything thrown by the library into a code the page can render. */
function asAcceptFailure(error: unknown): UnifiedCheckoutError {
  if (error instanceof UnifiedCheckoutError) return error;
  const detail =
    error instanceof Error && error.message ? error.message : 'the payment library refused';
  return new UnifiedCheckoutError('accept_failed', detail);
}

/**
 * Why the form did not appear, kept in one place.
 *
 * `show()` is the call that renders, and its two realistic failures — a CSP
 * `frame-src` refusal, and a library that never settles — both mean "the donor
 * is looking at a blank panel". Both are almost always fixed in the nginx
 * `frame-src`, so both say so.
 */
class ShowFailure extends Error {
  constructor(
    reason: string,
    readonly code: UnifiedCheckoutFailure,
  ) {
    super(reason);
    this.name = 'ShowFailure';
  }

  static timedOut(): ShowFailure {
    return new ShowFailure('the payment form did not appear in time', 'show_timeout');
  }

  static because(error: unknown): ShowFailure {
    return new ShowFailure(
      error instanceof Error && error.message ? error.message : 'the library refused to render',
      'show_failed',
    );
  }

  toUnifiedError(): UnifiedCheckoutError {
    if (this.code === 'show_timeout') {
      // Not the CSP sentence. A frame that did not answer in time is not a
      // broken deployment, and telling the donor to check a policy they cannot
      // see would be both useless and untrue.
      return new UnifiedCheckoutError(
        'show_timeout',
        'The card form did not load in time. Nothing was taken from your card. Please try again.',
      );
    }
    return new UnifiedCheckoutError(
      'show_failed',
      `${this.message}. This is usually a Content Security Policy blocking the card frame from https://testup.cybersource.com.`,
    );
  }
}

/**
 * Build the adapter.
 *
 * One load per page however many times `accept()` is called: a double click on
 * "pay" would otherwise start two concurrent loads of the same script, and the
 * second would overwrite the first's `window.Accept`.
 */
export function createUnifiedCheckoutAdapter(
  options: Partial<AdapterOptions> = {},
): UnifiedCheckoutAdapter {
  const inject = options.inject ?? defaultInjectScript;
  const injectFingerprint = options.injectFingerprint ?? injectFingerprintScript;
  const waitForAccept =
    options.waitForAccept ?? ((ms: number) => defaultWaitForAccept(ms, options.scope));
  const acceptTimeoutMs = options.acceptTimeoutMs ?? DEFAULT_ACCEPT_TIMEOUT_MS;
  const showTimeoutMs =
    options.showTimeoutMs ?? showTimeoutFromEnv(import.meta.env.VITE_UC_SHOW_TIMEOUT_MS);

  let ready: Promise<AcceptFactory> | undefined;

  return {
    async accept(response: CaptureContextResponse): Promise<string> {
      const { captureContext, clientLibrary, clientLibraryIntegrity } = response;
      if (!captureContext || !clientLibrary || !clientLibraryIntegrity) {
        throw new UnifiedCheckoutError(
          'context_incomplete',
          'The payment form could not be prepared. Please reload the page and try again.',
        );
      }

      // ---- The device-fingerprint script, started first and never awaited.
      //
      // It goes here, before the library, because the two run concurrently and
      // the fingerprint needs the 3-5 seconds of collection that the donor
      // spends typing their card. Awaiting it would serialise them and add those
      // seconds to the page; awaiting it *after* `show()` would leave no
      // collection window at all. Not awaiting it also means a fingerprint
      // script that never arrives cannot delay or fail a donation - see
      // {@link injectFingerprintScript}.
      if (response.fingerprintScriptUrl) {
        void injectFingerprint(response.fingerprintScriptUrl);
      }

      // 0 and 1, together: inject the script, then wait for the global.
      //
      // Memoised per adapter instance, because a second click on "pay" would
      // otherwise start a second load of the same script and the second
      // evaluation would replace the first's `window.Accept`. A failure clears
      // the memo so a retry on the same page gets a real second attempt.
      ready ??= (async () => {
        debugFrame('inject-start');
        debugTimer('inject', 'started');
        try {
          // The script load is time-boxed like everything else. A request that
          // neither loads nor errors - a hung proxy, a dropped connection - is
          // the one await in this chain that used to have no deadline at all,
          // and "the page is quietly waiting for a script tag" is the exact
          // shape of the stall nobody could explain afterwards.
          await withTimeout(
            inject(clientLibrary, clientLibraryIntegrity),
            acceptTimeoutMs,
            () =>
              new UnifiedCheckoutError(
                'script_blocked',
                'The secure payment library did not finish loading in time. Nothing has been taken from your card; please try again.',
              ),
          );
        } catch (error) {
          debugFrame('inject-blocked');
          if (error instanceof UnifiedCheckoutError) throw error;
          throw new UnifiedCheckoutError(
            'script_blocked',
            'The secure payment library could not be loaded. This is usually a Content Security Policy blocking https://testup.cybersource.com.',
          );
        }
        debugFrame('inject-done');
        debugTimer('inject', 'fired');
        return waitForAccept(acceptTimeoutMs);
      })().catch((error: unknown) => {
        ready = undefined;
        throw asAcceptFailure(error);
      });
      const accept = await ready;

      // 2. `unifiedPayments(false)`. `false` means "do not open the card form on
      //    its own" — the donor chooses a payment method first, which is what
      //    the containers on this page are laid out for.
      //
      //    Time-boxed, like every other wait: `Accept()` and `unifiedPayments()`
      //    are library promises with no contract to settle, and before this
      //    deadline they were the second await that could hang a checkout
      //    forever with nothing on screen to say so.
      debugFrame('accept-start');
      debugTimer('accept', 'started');
      let up: UnifiedPaymentsModule;
      try {
        up = await withTimeout(
          accept(captureContext).then((module) => module.unifiedPayments(false)),
          acceptTimeoutMs,
          () =>
            new UnifiedCheckoutError(
              'accept_failed',
              'The payment library did not start in time. Nothing has been taken from your card; please try again.',
            ),
        );
      } catch (error) {
        debugFrame('accept-refused');
        throw asAcceptFailure(error);
      }
      debugFrame('accept-ready');

      // 3. `show()`. This is the call that renders. Without it nothing appears
      //    and nothing throws, which is precisely the failure that shipped.
      debugFrame('show-start');
      debugTimer('show', 'started');
      let token: string;
      try {
        token = await withTimeout(
          Promise.resolve(up.show({ containers: UC_CONTAINERS })),
          showTimeoutMs,
          () => ShowFailure.timedOut(),
        );
      } catch (error) {
        up.dispose?.();
        debugTimer('show', 'fired');
        if (error instanceof ShowFailure) {
          debugFrame(error.code === 'show_timeout' ? 'show-timeout' : 'show-refused');
          throw error.toUnifiedError();
        }
        if (error instanceof UnifiedCheckoutError) {
          debugFrame('show-refused');
          throw error;
        }
        debugFrame('show-refused');
        throw ShowFailure.because(error).toUnifiedError();
      }

      if (typeof token !== 'string' || token.length === 0) {
        up.dispose?.();
        debugFrame('show-empty');
        throw new UnifiedCheckoutError(
          'no_transient_token',
          'No payment token was produced. Please try again or use another card.',
        );
      }

      // Recorded as an event only: the token's VALUE never reaches the
      // recorder - `debugFrame` takes no value argument, by design.
      debugFrame('show-token');
      return token;
    },
  };
}

/** The adapter the app uses. Tests inject their own rather than stubbing globals. */
export const unifiedCheckout: UnifiedCheckoutAdapter = createUnifiedCheckoutAdapter();
